import type {
  PermissionShapeViewModel,
  PermissionSlotViewModel,
  PermissionTermViewModel,
  PermissionVocabularyViewModel,
} from './api';

/**
 * Pure assembly for the grammar builder (PTV2-1443). A canonical shape's template
 * (e.g. `<scope>:*:*:*:<action>`) carries `<placeholder>` segments; each placeholder maps to a grammar
 * slot (optionally a sub-group of it), and the builder offers that slot's vocabulary values as a
 * dropdown. Assembling substitutes the chosen values back into the template, so only structurally
 * valid permissions can be produced — the server stays authoritative, but the UI cannot compose an
 * invalid shape.
 */

/** One fillable input for a shape, derived from a `<placeholder>` in its template. */
export interface BuilderInput {
  /** The template placeholder name, e.g. `entity`, `action`, `collection`, `scope`. */
  readonly placeholder: string;
  /** The grammar slot the placeholder draws from. */
  readonly slotName: string;
  /** The vocabulary options offered for this input (already group-filtered). */
  readonly options: readonly PermissionTermViewModel[];
}

/** Placeholder → grammar slot (+ optional term-group filter). The grammar's canonical placeholders. */
const PLACEHOLDER_SLOT: Readonly<Record<string, { slot: string; group?: string }>> = {
  entity: { slot: 'entity' },
  action: { slot: 'action' },
  collection: { slot: 'entityGroup', group: 'collection' },
  scope: { slot: 'entityGroup', group: 'scope' },
};

const WILDCARD = '*';

function isPlaceholder(segment: string): boolean {
  return segment.startsWith('<') && segment.endsWith('>');
}

function placeholderName(segment: string): string {
  return segment.slice(1, -1);
}

/** The ordered, fillable inputs for a shape, each with its vocabulary-derived options. */
export function builderInputs(
  shape: PermissionShapeViewModel,
  vocabulary: PermissionVocabularyViewModel,
): readonly BuilderInput[] {
  const inputs: BuilderInput[] = [];
  for (const segment of shape.template.split(':')) {
    if (!isPlaceholder(segment)) {
      continue;
    }
    const name = placeholderName(segment);
    const mapping = PLACEHOLDER_SLOT[name];
    if (mapping === undefined) {
      continue;
    }
    const slot: PermissionSlotViewModel | undefined = vocabulary.slots.find((s) => s.name === mapping.slot);
    const options = (slot?.values ?? []).filter((term) =>
      mapping.group === undefined ? true : term.group === mapping.group,
    );
    inputs.push({ placeholder: name, slotName: mapping.slot, options });
  }
  return inputs;
}

/** Assemble the five-segment permission from a shape template + the chosen value per placeholder. */
export function assemblePermission(
  shape: PermissionShapeViewModel,
  selected: Readonly<Record<string, string>>,
): string {
  return shape.template
    .split(':')
    .map((segment) => (isPlaceholder(segment) ? (selected[placeholderName(segment)] ?? WILDCARD) : segment))
    .join(':');
}

/** The default selection for a set of inputs: the first offered option of each. */
export function defaultSelection(inputs: readonly BuilderInput[]): Record<string, string> {
  const selection: Record<string, string> = {};
  for (const input of inputs) {
    selection[input.placeholder] = input.options[0]?.value ?? WILDCARD;
  }
  return selection;
}
